import { useState, useEffect, useCallback } from 'react';
import { gradeApi } from '../../../api.js';
import toast from 'react-hot-toast';

const useGradeRatings = (examPaper, getFeedback, submission_id, assignment_id, user) => {
  // State for thumbs up/down rating
  const [gradeRatings, setGradeRatings] = useState({});
  const [showThumbsDownMessage, setShowThumbsDownMessage] = useState({});
  const [showChangeReasonPrompt, setShowChangeReasonPrompt] = useState({});
  const [changeReasons, setChangeReasons] = useState({});
  const [savingRating, setSavingRating] = useState({});
  const [showAiLearningPrompt, setShowAiLearningPrompt] = useState({});
  const [aiLearningLoading, setAiLearningLoading] = useState({});

  // Initialize grade ratings from feedback data when component mounts or feedback changes
  useEffect(() => {
    if (!examPaper || !getFeedback || !submission_id) {
      return;
    }

    // Add a small delay to ensure feedback data is loaded
    const timeoutId = setTimeout(() => {
      const initializeGradeRatings = () => {
        const initialRatings = {};

        examPaper.forEach(question => {
          question.subQuestions.forEach(subQuestion => {
            const feedback = getFeedback(subQuestion.id);

            if (feedback && feedback.grade_rating) {
              // Get the rating from the grade_rating object
              if (feedback.grade_rating.rating) {
                initialRatings[subQuestion.id] = feedback.grade_rating.rating;
              }
            }
          });
        });
        setGradeRatings(initialRatings);
      };

      initializeGradeRatings();
    }, 100); // Small delay to ensure feedback is loaded

    return () => clearTimeout(timeoutId);
  }, [examPaper, getFeedback, submission_id]);

  // Function to check if subquestion meets progressive learning requirements
  const checkAILearningStatus = async (subQuestionId) => {
    try {
      const response = await gradeApi.get({
        path: `/api/assignment/${assignment_id}/subquestion/${subQuestionId}/ai-learning-status/`,
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });
      return {
        canTriggerLearning: response.data.can_trigger_learning,
        currentStats: response.data.current_stats,
        requiredThresholds: response.data.required_thresholds
      };
    } catch (error) {
      console.error('Error checking AI learning status:', error);
      return {
        canTriggerLearning: false,
        currentStats: { total_ratings: 0, teacher_comments: 0, learning_attempts: 0 },
        requiredThresholds: { comments: 1, ratings: 5 }
      };
    }
  };

  // Function to trigger AI learning and regrading with polling
  const triggerAiLearning = async (subQuestionId, triggerAiLearningWithPolling) => {
    setAiLearningLoading(prev => ({
      ...prev,
      [subQuestionId]: true
    }));

    try {
      // Use the polling version from useAiLearning hook
      await triggerAiLearningWithPolling(subQuestionId);

      toast.success('AI learning initiated! The remaining submissions will be regraded.');

      // Close the prompt
      setShowAiLearningPrompt(prev => ({
        ...prev,
        [subQuestionId]: false
      }));
    } catch (error) {
      console.error('Error triggering AI learning:', error);
      toast.error('Failed to initiate AI learning. Please try again.');
    } finally {
      setAiLearningLoading(prev => ({
        ...prev,
        [subQuestionId]: false
      }));
    }
  };

  // Function to save grade rating to backend
  const saveGradeRating = async (subQuestionId, rating, reasoning, gradeWasChanged = false) => {
    // Set loading state
    setSavingRating(prev => ({
      ...prev,
      [subQuestionId]: true
    }));

    try {
      // Get the current feedback to determine grade value
      const feedback = getFeedback(subQuestionId);
      if (!feedback) {
        console.error('No feedback found for subquestion:', subQuestionId);
        return;
      }

      const gradeValue = parseFloat(feedback.score);

      // Determine original AI score correctly
      // If this subquestion already has a rating AND has a valid original_ai_score, preserve it
      // Otherwise, use the current score as the original AI score
      let originalAiScore;
      if (feedback.grade_rating &&
          feedback.grade_rating.original_ai_score !== null &&
          feedback.grade_rating.original_ai_score !== undefined) {
        // Already has a rating with valid original AI score - preserve it
        originalAiScore = feedback.grade_rating.original_ai_score;
      } else {
        // Either no rating exists, or rating exists but original_ai_score is null/undefined
        // In both cases, use current score as original AI score
        originalAiScore = gradeValue;
      }

      // Determine original AI feedback correctly
      // If this subquestion already has a rating AND has a valid original_ai_feedback, preserve it
      // Otherwise, use the current feedback as the original AI feedback
      let originalAiFeedback;
      if (feedback.grade_rating &&
          feedback.grade_rating.original_ai_feedback !== null &&
          feedback.grade_rating.original_ai_feedback !== undefined) {
        // Already has a rating with valid original AI feedback - preserve it
        originalAiFeedback = feedback.grade_rating.original_ai_feedback;
      } else {
        // Either no rating exists, or rating exists but original_ai_feedback is null/undefined
        // In both cases, use current feedback as original AI feedback
        originalAiFeedback = feedback.feedback || '';
      }

      const payload = {
        assignment_submission_id: parseInt(submission_id),
        assignment_subquestion_id: parseInt(subQuestionId),
        rating: rating,
        reasoning: reasoning,
        grade_value: gradeValue,
        original_ai_score: originalAiScore,
        original_ai_feedback: originalAiFeedback,
        grade_was_changed: gradeWasChanged
      };

      await gradeApi.post({
        path: `/api/assignment/${assignment_id}/grade-rating/`,
        body: payload,
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });

      console.log('Grade rating saved successfully:', payload);
    } catch (error) {
      console.error('Error saving grade rating:', error);
      toast.error('Failed to save grade rating');

      // Revert the rating state on error
      setGradeRatings(prev => ({
        ...prev,
        [subQuestionId]: null
      }));
    } finally {
      // Clear loading state
      setSavingRating(prev => ({
        ...prev,
        [subQuestionId]: false
      }));
    }
  };

  // Function to handle thumbs up click
  const handleThumbsUp = async subQuestionId => {
    const newRating = gradeRatings[subQuestionId] === 'up' ? null : 'up';

    setGradeRatings(prev => ({
      ...prev,
      [subQuestionId]: newRating
    }));

    // Clear thumbs down message if it was showing
    setShowThumbsDownMessage(prev => ({
      ...prev,
      [subQuestionId]: false
    }));

    // Save to backend
    await saveGradeRating(subQuestionId, newRating, null);

    // Check if we meet progressive learning requirements after thumbs up
    if (newRating === 'up') {
      const learningStatus = await checkAILearningStatus(subQuestionId);

      // Show AI learning prompt if threshold is met
      if (learningStatus.canTriggerLearning) {
        setShowAiLearningPrompt(prev => ({
          ...prev,
          [subQuestionId]: true
        }));
      }
    }
  };

  // Function to handle thumbs down click
  const handleThumbsDown = async subQuestionId => {
    const newRating = gradeRatings[subQuestionId] === 'down' ? null : 'down';

    setGradeRatings(prev => ({
      ...prev,
      [subQuestionId]: newRating
    }));

    // Show message to select new grade only if thumbs down is active
    setShowThumbsDownMessage(prev => ({
      ...prev,
      [subQuestionId]: newRating === 'down'
    }));

    // Save to backend
    await saveGradeRating(subQuestionId, newRating, null);

    // Note: AI learning check is NOT triggered here - only after reasoning submission/skip
  };

  // Function to automatically thumbs up when grade is changed manually
  const handleManualGradeChange = async subQuestionId => {
    // Show inline prompt asking why they changed it
    setShowChangeReasonPrompt(prev => ({
      ...prev,
      [subQuestionId]: true
    }));

    setGradeRatings(prev => ({
      ...prev,
      [subQuestionId]: 'up'
    }));

    // Clear thumbs down message if it was showing
    setShowThumbsDownMessage(prev => ({
      ...prev,
      [subQuestionId]: false
    }));

    // Save the thumbs up rating immediately when grade is manually changed
    await saveGradeRating(subQuestionId, 'up', null, true);
  };

  // Stable handlers for change reason prompt
  const handleChangeReasonSubmit = useCallback(
    async (subQuestionId, reasoning) => {
      // Get the current rating at the time of submission
      const currentRating = gradeRatings[subQuestionId];

      // Update the changeReasons state with the submitted reasoning
      setChangeReasons(prev => ({
        ...prev,
        [subQuestionId]: reasoning
      }));

      // Close reason prompt first
      setShowChangeReasonPrompt(prev => ({
        ...prev,
        [subQuestionId]: false
      }));

      // Save the rating with reasoning to backend using the passed reasoning value
      await saveGradeRating(subQuestionId, currentRating, reasoning, true);

      // Check if we meet progressive learning requirements
      const learningStatus = await checkAILearningStatus(subQuestionId);

      // Show AI learning prompt if threshold is met
      if (learningStatus.canTriggerLearning) {
        setShowAiLearningPrompt(prev => ({
          ...prev,
          [subQuestionId]: true
        }));
      }
    },
    [gradeRatings, saveGradeRating, checkAILearningStatus]
  );

  const handleChangeReasonSkip = useCallback(async subQuestionId => {
    // Close reason prompt
    setShowChangeReasonPrompt(prev => ({
      ...prev,
      [subQuestionId]: false
    }));

    // Check if we meet progressive learning requirements after skipping
    const learningStatus = await checkAILearningStatus(subQuestionId);

    // Show AI learning prompt if threshold is met
    if (learningStatus.canTriggerLearning) {
      setShowAiLearningPrompt(prev => ({
        ...prev,
        [subQuestionId]: true
      }));
    }
  }, [checkAILearningStatus]);

  const handleReasonChange = useCallback((subQuestionId, value) => {
    setChangeReasons(prev => ({
      ...prev,
      [subQuestionId]: value
    }));
  }, []);

  const handleAiLearningDismiss = useCallback((subQuestionId) => {
    setShowAiLearningPrompt(prev => ({
      ...prev,
      [subQuestionId]: false
    }));
  }, []);

  return {
    gradeRatings,
    showThumbsDownMessage,
    showChangeReasonPrompt,
    changeReasons,
    savingRating,
    showAiLearningPrompt,
    aiLearningLoading,
    handleThumbsUp,
    handleThumbsDown,
    handleManualGradeChange,
    handleChangeReasonSubmit,
    handleChangeReasonSkip,
    handleReasonChange,
    triggerAiLearning,
    handleAiLearningDismiss
  };
};

export default useGradeRatings;